import { useEffect, useState, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import axios from "axios";
import Select from "react-select";
import { server } from "../../server";

// Redux fetchers (already used across your finance pages)
import { fetchMiscelleneousFeesDetails } from "../../redux/miscelleneousFeesSlice";
import { fetchAdmissionFormDetails } from "../../redux/admissionFormSlice";
import { fetchDuesFeeDetails } from "../../redux/duesFeeSlice";
import { fetchPaidFeeDetails } from "../../redux/paidFeeSlice";
import { fetchFeeMasterDetails } from "../../redux/feeMasterSlice";
import { fetchExpenses } from "../../redux/expensesSlice";

import { classList, sectionList, monthsList } from "../../utils";
import { formatDate } from "../../functions";
import { getDecodedToken } from "../../auth";

function FinanceReports() {
  const dispatch = useDispatch();

  // ----------------------- REDUX STATE -----------------------
  const { data: miscelleneousFeesDetails, loading: loadingMisc } = useSelector(
    (state) => state.miscelleneousFees,
  );
  const { data: admissionFormDetails, loading: loadingAdm } = useSelector(
    (state) => state.admissionForm,
  );
  const { data: duesFeeDetails, loading: loadingDues } = useSelector(
    (state) => state.duesFee,
  );
  const { data: paidFeeDetails, loading: loadingPaid } = useSelector(
    (state) => state.paidFee,
  );
  const { data: feeMasterDetails, loading: loadingMaster } = useSelector(
    (state) => state.feeMaster,
  );
  const { data: expenses, loading: loadingExpenses } = useSelector(
    (state) => state.expenses,
  );

  // Monthly Fees & Collected By Summary need direct API calls (per your existing code)
  const [monthlyFees, setMonthlyFees] = useState([]);
  const [collectedBySummary, setCollectedBySummary] = useState([]);
  const [loadingMonthly, setLoadingMonthly] = useState(false);
  const [loadingCollected, setLoadingCollected] = useState(false);

  // ----------------------- LOCAL STATE -----------------------
  const [activeDataset, setActiveDataset] = useState("paid");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedPaymentMode, setSelectedPaymentMode] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [user, setUser] = useState(null);

  const today = new Date().toISOString().split("T")[0];

  useEffect(() => {
    const decoded = getDecodedToken();
    if (decoded) setUser(decoded);
  }, []);

  // ----------------------- INITIAL FETCHES -----------------------
  useEffect(() => {
    dispatch(fetchMiscelleneousFeesDetails());
    dispatch(fetchAdmissionFormDetails());
    dispatch(fetchDuesFeeDetails());
    dispatch(fetchPaidFeeDetails());
    dispatch(fetchFeeMasterDetails());
    dispatch(fetchExpenses());
  }, [dispatch]);

  useEffect(() => {
    fetchMonthlyFeesData();
    fetchCollectedBySummary();
  }, []);

  const fetchMonthlyFeesData = async () => {
    try {
      setLoadingMonthly(true);
      const todayDate = new Date().toISOString().split("T")[0];
      const response = await axios.post(
        `${server}/feeHistory_dateWise_mongodb`,
        { date: todayDate },
        {
          headers: { Authorization: `${localStorage.getItem("token")}` },
        },
      );
      const responseData = response.data?.["body-json"]?.body?.data;
      if (Array.isArray(responseData)) setMonthlyFees(responseData);
    } catch (error) {
      // silent
    } finally {
      setLoadingMonthly(false);
    }
  };

  const fetchCollectedBySummary = async () => {
    try {
      setLoadingCollected(true);
      const t = new Date();
      const formattedToday = t.toISOString().split("T")[0];
      const yesterday = new Date(t);
      yesterday.setDate(yesterday.getDate() - 1);
      const formattedYesterday = yesterday.toISOString().split("T")[0];

      const response = await axios.get(
        `${server}/FilterWithCollectedByAndDateRange?fromDate=${formattedYesterday}&toDate=${formattedToday}`,
        {
          headers: { Authorization: localStorage.getItem("token") },
        },
      );
      if (Array.isArray(response.data)) setCollectedBySummary(response.data);
    } catch (error) {
      // silent
    } finally {
      setLoadingCollected(false);
    }
  };

  // ----------------------- DATASETS -----------------------
  const datasets = {
    paid: {
      data: paidFeeDetails,
      label: "Paid Fees",
      loading: loadingPaid,
    },
    dues: {
      data: duesFeeDetails,
      label: "Dues",
      loading: loadingDues,
    },
    monthly: {
      data: monthlyFees,
      label: "Today's Collection",
      loading: loadingMonthly,
    },
    misc: {
      data: miscelleneousFeesDetails,
      label: "Miscellaneous",
      loading: loadingMisc,
    },
    admission: {
      data: admissionFormDetails,
      label: "Admission Forms",
      loading: loadingAdm,
    },
    collectedBy: {
      data: collectedBySummary,
      label: "Collected By",
      loading: loadingCollected,
    },
    expense: {
      data: expenses,
      label: "Expenses",
      loading: loadingExpenses,
    },
    master: {
      data: feeMasterDetails,
      label: "Fee Master",
      loading: loadingMaster,
    },
  };

  const currentData = datasets[activeDataset]?.data || [];
  const currentLoading = datasets[activeDataset]?.loading;

  // ----------------------- HELPERS -----------------------
  const toUpper = (v) =>
    v !== null && v !== undefined ? String(v).toUpperCase() : "";

  const writeWorkbook = (workbook, filename) => {
    const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([buffer], { type: "application/octet-stream" });
    saveAs(blob, filename);
    toast.success("Report Generated");
  };

  // Normalize each dataset to a common shape for filtering & previewing
  const normalize = (item, type) => {
    switch (type) {
      case "paid":
      case "dues":
        return {
          ...item,
          _class: item.studentDetails?.currentClass || "",
          _section: item.studentDetails?.currentSection || "",
          _name: item.studentDetails?.name || "",
          _admissionNo: item.studentDetails?.admission_no || "",
          _amount:
            (item.monthlyFee || 0) *
              ((type === "paid" ? item.paidMonths : item.unpaidMonths) || [])
                .length || 0,
          _date: item.createdAt || "",
        };
      case "monthly":
        return {
          ...item,
          _class: item.class || "",
          _section: item.currentSection || "",
          _name: item.student_name || "",
          _admissionNo: item.admission_no || "",
          _amount: (() => {
            const fd = item.feeDetails || {};
            let total = 0;
            if (fd.developmentCharges?.paid)
              total += fd.developmentCharges.TotalAmt || 0;
            if (fd.admissionFee?.paid) total += fd.admissionFee.TotalAmt || 0;
            (fd.monthPaid || []).forEach((m) => (total += m.TotalAmt || 0));
            return total;
          })(),
          _date: item.date || "",
          _mode: (() => {
            const fd = item.feeDetails || {};
            const modes = new Set();
            [fd.developmentCharges, fd.admissionFee, ...(fd.monthPaid || [])]
              .filter((f) => f?.paid)
              .forEach((f) =>
                Object.keys(f.paymentMode || {}).forEach((m) => modes.add(m)),
              );
            return Array.from(modes).join(", ");
          })(),
        };
      case "misc":
        return {
          ...item,
          _class: "",
          _section: "",
          _name: item.studentName || "",
          _admissionNo: item.formNo || "",
          _amount: Number(item.amount) || 0,
          _date: item.createdAt || "",
          _mode: item.mode || "",
        };
      case "admission":
        return {
          ...item,
          _class: item.student?.Class || "",
          _section: "",
          _name: item.student?.name || "",
          _admissionNo: item.formNo || "",
          _amount: Number(item.amount) || 0,
          _date: item.applyDate
            ? item.applyDate.split("/").reverse().join("-")
            : "",
          _mode: item.mode || "",
        };
      case "expense":
        return {
          ...item,
          _class: "",
          _section: "",
          _name: item.name || "",
          _admissionNo: item.expenseId || "",
          _amount: Number(item.amount) || 0,
          _date: item.date || "",
          _mode: "",
          _head: item.expenseHead || "",
        };
      case "master":
        return {
          ...item,
          _class: item.class || "",
          _section: "",
          _name: item.class || "",
          _admissionNo: "",
          _amount: Number(item.monthlyFee || 0),
          _date: "",
        };
      case "collectedBy":
        return {
          ...item,
          _class: "",
          _section: "",
          _name: item.collectedBy || "",
          _admissionNo: "",
          _amount: Number(item.grandTotal) || 0,
          _date: "",
        };
      default:
        return item;
    }
  };

  const normalizedData = useMemo(
    () => currentData.map((d) => normalize(d, activeDataset)),
    [currentData, activeDataset],
  );

  // ----------------------- DYNAMIC OPTIONS -----------------------
  const classOptions = useMemo(() => {
    const set = new Set();
    normalizedData.forEach((s) => {
      if (s._class) set.add(s._class);
    });
    const ordered = classList.filter((c) => set.has(c));
    set.forEach((c) => {
      if (!ordered.includes(c)) ordered.push(c);
    });
    return ordered.map((c) => ({ value: c, label: c }));
  }, [normalizedData]);

  const sectionOptions = useMemo(() => {
    if (!selectedClass) return [];
    const set = new Set();
    normalizedData.forEach((s) => {
      if (s._class === selectedClass && s._section) set.add(s._section);
    });
    const ordered = sectionList.filter((sec) => set.has(sec));
    set.forEach((sec) => {
      if (!ordered.includes(sec)) ordered.push(sec);
    });
    return ordered.map((sec) => ({ value: sec, label: sec }));
  }, [normalizedData, selectedClass]);

  const monthOptions = useMemo(
    () =>
      monthsList.slice(0, 12).map((m) => ({
        value: m,
        label: m.charAt(0).toUpperCase() + m.slice(1),
      })),
    [],
  );

  const paymentModeOptions = [
    { value: "Cash", label: "Cash" },
    { value: "UPI", label: "UPI" },
    { value: "NEFT", label: "NEFT" },
    { value: "Cheque", label: "Cheque" },
    { value: "Card Swap", label: "Card Swap" },
    { value: "Net Banking", label: "Net Banking" },
    { value: "Online", label: "Online" },
  ];

  const selectStyles = {
    control: (base) => ({
      ...base,
      border: "none",
      boxShadow: "none",
      backgroundColor: "transparent",
      minHeight: "32px",
      cursor: "pointer",
    }),
    valueContainer: (base) => ({ ...base, padding: "0" }),
    indicatorSeparator: () => ({ display: "none" }),
    dropdownIndicator: (base) => ({ ...base, padding: "4px" }),
    menu: (base) => ({
      ...base,
      borderRadius: "12px",
      overflow: "hidden",
      boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
      border: "none",
      zIndex: 5,
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#ffc107"
        : state.isFocused
          ? "#f8f9fa"
          : "white",
      color: state.isSelected ? "white" : "#212529",
      cursor: "pointer",
      fontSize: "14px",
    }),
    placeholder: (base) => ({ ...base, color: "#6c757d", fontSize: "14px" }),
    singleValue: (base) => ({ ...base, color: "#212529", fontSize: "14px" }),
  };

  // ----------------------- FILTERS -----------------------
  const filteredData = useMemo(() => {
    let data = [...normalizedData];

    if (selectedClass) data = data.filter((s) => s._class === selectedClass);

    if (selectedSection)
      data = data.filter((s) => s._section === selectedSection);

    if (selectedMonth) {
      data = data.filter((s) => {
        if (activeDataset === "paid")
          return (s.paidMonths || []).includes(selectedMonth);
        if (activeDataset === "dues")
          return (s.unpaidMonths || []).includes(selectedMonth);
        if (s._date) {
          const m = new Date(s._date).toLocaleString("default", {
            month: "long",
          });
          return m.toLowerCase() === selectedMonth.toLowerCase();
        }
        return false;
      });
    }

    if (selectedPaymentMode) {
      data = data.filter((s) =>
        (s._mode || "")
          .toLowerCase()
          .includes(selectedPaymentMode.toLowerCase()),
      );
    }

    if (fromDate || toDate) {
      data = data.filter((s) => {
        if (!s._date) return false;
        const d = new Date(s._date).toISOString().split("T")[0];
        if (fromDate && d < fromDate) return false;
        if (toDate && d > toDate) return false;
        return true;
      });
    }

    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      data = data.filter((s) => {
        const flatten = (obj) => {
          const vals = [];
          for (const k in obj) {
            const v = obj[k];
            if (v && typeof v === "object") vals.push(...flatten(v));
            else if (typeof v === "string" || typeof v === "number")
              vals.push(String(v));
          }
          return vals;
        };
        return flatten(s).some((v) => v.toLowerCase().includes(lower));
      });
    }

    return data;
  }, [
    normalizedData,
    selectedClass,
    selectedSection,
    selectedMonth,
    selectedPaymentMode,
    fromDate,
    toDate,
    searchTerm,
    activeDataset,
  ]);

  // ----------------------- SUMMARY STATS -----------------------
  const summary = useMemo(() => {
    const totalAmount = filteredData.reduce(
      (sum, s) => sum + (s._amount || 0),
      0,
    );
    const records = filteredData.length;

    // Mode-wise totals (only relevant for paid/monthly/misc/admission)
    const modeTotals = {};
    if (["monthly", "misc", "admission"].includes(activeDataset)) {
      filteredData.forEach((s) => {
        if (s._mode) {
          s._mode.split(",").forEach((m) => {
            const key = m.trim();
            if (!key) return;
            modeTotals[key] = (modeTotals[key] || 0) + (s._amount || 0);
          });
        }
      });
    }

    // Income vs Expense (only for analytics)
    let totalIncome = 0;
    let totalExpense = 0;

    (paidFeeDetails || []).forEach((s) => {
      const months = (s.paidMonths || []).length;
      totalIncome +=
        (s.monthlyFee || 0) * months +
        (s.transportFee || 0) *
          (s.paidMonths || []).filter((m) => m !== "June").length;
    });
    (miscelleneousFeesDetails || []).forEach(
      (s) => (totalIncome += Number(s.amount) || 0),
    );
    (admissionFormDetails || []).forEach(
      (s) => (totalIncome += Number(s.amount) || 0),
    );
    (expenses || []).forEach((s) => (totalExpense += Number(s.amount) || 0));

    const avgPerRecord = records ? totalAmount / records : 0;

    return {
      totalAmount,
      records,
      modeTotals,
      totalIncome,
      totalExpense,
      netBalance: totalIncome - totalExpense,
      avgPerRecord,
    };
  }, [
    filteredData,
    activeDataset,
    paidFeeDetails,
    miscelleneousFeesDetails,
    admissionFormDetails,
    expenses,
  ]);

  // ----------------------- REPORT GENERATORS -----------------------
  const formatRow = (s) => {
    // Generic formatter — picks fields based on dataset
    switch (activeDataset) {
      case "paid":
      case "dues": {
        const months = activeDataset === "paid" ? s.paidMonths : s.unpaidMonths;
        const monthsArr = Array.isArray(months) ? months : [];
        const monthlyFee = s.monthlyFee || 0;
        const transportFee = s.transportFee || 0;
        const transportableMonths = monthsArr.filter((m) => m !== "June");
        return {
          "ADMISSION NO": toUpper(s.studentDetails?.admission_no),
          "STUDENT NAME": toUpper(s.studentDetails?.name),
          CLASS: toUpper(s.studentDetails?.currentClass),
          SECTION: toUpper(s.studentDetails?.currentSection),
          "ROLL NO": toUpper(s.studentDetails?.rollNo),
          GENDER: toUpper(s.studentDetails?.gender),
          "PHONE NO": toUpper(s.studentDetails?.phoneNo),
          "BUS STOP": toUpper(s.transportFacility?.busStop || "NONE"),
          "BUS NO": toUpper(s.transportFacility?.busNumber || "N/A"),
          [activeDataset === "paid" ? "PAID MONTHS" : "UNPAID MONTHS"]:
            monthsArr.join(", "),
          "MONTHLY FEE": monthlyFee,
          "TRANSPORT FEE": transportFee,
          "TOTAL AMOUNT":
            monthlyFee * monthsArr.length +
            transportFee * transportableMonths.length,
          "SCHOLARSHIP TYPE": toUpper(s.scholarshipType || "NONE"),
        };
      }
      case "monthly": {
        const fd = s.feeDetails || {};
        const dev = fd.developmentCharges?.paid
          ? fd.developmentCharges.TotalAmt
          : 0;
        const adm = fd.admissionFee?.paid ? fd.admissionFee.TotalAmt : 0;
        const monthly = (fd.monthPaid || []).reduce(
          (a, m) => a + (m.monthlyFee || 0),
          0,
        );
        const transport = (fd.monthPaid || []).reduce(
          (a, m) => a + (m.transportFee || 0),
          0,
        );
        return {
          "RECEIPT DATE": s.date
            ? new Date(s.date).toLocaleString("en-GB")
            : "",
          "RECEIPT NO": toUpper(s.receipt_no),
          "ADMISSION NO": toUpper(s.admission_no),
          "STUDENT NAME": toUpper(s.student_name),
          CLASS: toUpper(s.class),
          SECTION: toUpper(s.currentSection),
          "ROLL NO": toUpper(s.rollNo),
          "DEVELOPMENT FEE": dev,
          "ADMISSION FEE": adm,
          "MONTHLY FEE": monthly,
          "TRANSPORT FEE": transport,
          "TOTAL PAID": dev + adm + monthly + transport,
          "MODE OF PAYMENT": toUpper(s._mode),
        };
      }
      case "misc":
        return {
          "RECEIPT NO": toUpper(s.receipt_no),
          "FORM NO": toUpper(s.formNo),
          "STUDENT NAME": toUpper(s.studentName),
          TYPE: toUpper(s.type),
          AMOUNT: Number(s.amount) || 0,
          MODE: toUpper(s.mode),
          "PAYMENT ID": toUpper(s.paymentId),
          BANK: toUpper(s.bank),
          "COLLECTED BY": toUpper(s.collectedBy),
          DATE: s.createdAt
            ? new Date(s.createdAt).toLocaleDateString("en-GB")
            : "",
        };
      case "admission":
        return {
          "FORM NO": toUpper(s.formNo),
          "APPLY DATE": toUpper(s.applyDate),
          "STUDENT NAME": toUpper(s.student?.name),
          "FATHER NAME": toUpper(s.student?.fatherName),
          GENDER: toUpper(s.student?.gender),
          CLASS: toUpper(s.student?.Class),
          "CONTACT NO": toUpper(s.student?.contactNo),
          AMOUNT: Number(s.amount) || 0,
          MODE: toUpper(s.mode),
          SUBMITTED: s.submitted ? "YES" : "NO",
        };
      case "expense":
        return {
          "EXPENSE ID": toUpper(s.expenseId),
          "EXPENSE HEAD": toUpper(s.expenseHead),
          NAME: toUpper(s.name),
          "INVOICE NO": toUpper(s.invoiceNumber),
          DATE: s.date ? new Date(s.date).toLocaleDateString("en-GB") : "",
          AMOUNT: Number(s.amount) || 0,
          DESCRIPTION: s.description || "",
        };
      case "master":
        return {
          CLASS: toUpper(s.class),
          "ADMISSION FEE": Number(s.admissionFee) || 0,
          "MONTHLY FEE": Number(s.monthlyFee) || 0,
          "DEVELOPMENT CHARGES": Number(s.developmentCharges) || 0,
        };
      case "collectedBy":
        return {
          "COLLECTED BY": toUpper(s.collectedBy),
          "GRAND TOTAL": Number(s.grandTotal) || 0,
        };
      default:
        return {};
    }
  };

  const sortByAmount = (arr) =>
    arr.sort(
      (a, b) =>
        (b["AMOUNT"] || b["TOTAL AMOUNT"] || 0) -
        (a["AMOUNT"] || a["TOTAL AMOUNT"] || 0),
    );

  // ---- Specific Reports ----
  const reportAll = () => {
    if (!filteredData.length) {
      toast.error("No records to export");
      return;
    }
    const wb = XLSX.utils.book_new();
    const rows = filteredData.map(formatRow);
    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(
      wb,
      ws,
      datasets[activeDataset].label.slice(0, 31),
    );
    writeWorkbook(wb, `${datasets[activeDataset].label}_${today}.xlsx`);
  };

  const reportClassWise = () => {
    if (!filteredData.length) {
      toast.error("No records to export");
      return;
    }
    const wb = XLSX.utils.book_new();
    const groups = {};
    filteredData.forEach((s) => {
      const cls = s._class || "UNKNOWN";
      if (!groups[cls]) groups[cls] = [];
      groups[cls].push(formatRow(s));
    });
    Object.entries(groups).forEach(([cls, rows]) => {
      const ws = XLSX.utils.json_to_sheet(rows);
      XLSX.utils.book_append_sheet(wb, ws, toUpper(cls).slice(0, 31));
    });
    writeWorkbook(wb, `ClassWise_${today}.xlsx`);
  };

  const reportPaymentModeWise = () => {
    if (!filteredData.length) {
      toast.error("No records to export");
      return;
    }
    const wb = XLSX.utils.book_new();
    const groups = {};
    filteredData.forEach((s) => {
      const mode = s._mode || "UNKNOWN";
      mode.split(",").forEach((m) => {
        const key = m.trim() || "UNKNOWN";
        if (!groups[key]) groups[key] = [];
        groups[key].push(formatRow(s));
      });
    });
    Object.entries(groups).forEach(([mode, rows]) => {
      const ws = XLSX.utils.json_to_sheet(rows);
      XLSX.utils.book_append_sheet(wb, ws, toUpper(mode).slice(0, 31));
    });
    writeWorkbook(wb, `PaymentModeWise_${today}.xlsx`);
  };

  const reportMonthWise = () => {
    if (!filteredData.length) {
      toast.error("No records to export");
      return;
    }
    const wb = XLSX.utils.book_new();
    const groups = {};

    filteredData.forEach((s) => {
      let key = "UNKNOWN";
      if (s._date) {
        key = new Date(s._date).toLocaleString("default", { month: "long" });
      } else if (activeDataset === "paid" || activeDataset === "dues") {
        const months = activeDataset === "paid" ? s.paidMonths : s.unpaidMonths;
        (months || []).forEach((m) => {
          if (!groups[m]) groups[m] = [];
          groups[m].push(formatRow(s));
        });
        return;
      }
      if (!groups[key]) groups[key] = [];
      groups[key].push(formatRow(s));
    });

    Object.entries(groups).forEach(([month, rows]) => {
      const ws = XLSX.utils.json_to_sheet(rows);
      XLSX.utils.book_append_sheet(wb, ws, toUpper(month).slice(0, 31));
    });
    writeWorkbook(wb, `MonthWise_${today}.xlsx`);
  };

  const reportDateRange = () => {
    if (!fromDate || !toDate) {
      toast.error("Please select From and To dates");
      return;
    }
    if (!filteredData.length) {
      toast.error("No records in this date range");
      return;
    }
    const wb = XLSX.utils.book_new();
    const rows = filteredData.map(formatRow);
    rows.sort((a, b) => (a.DATE || "").localeCompare(b.DATE || ""));
    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, "DATE RANGE");
    writeWorkbook(wb, `DateRange_${fromDate}_to_${toDate}.xlsx`);
  };

  const reportTopPayers = () => {
    if (!filteredData.length) {
      toast.error("No records to export");
      return;
    }
    const wb = XLSX.utils.book_new();
    const rows = filteredData
      .map(formatRow)
      .sort((a, b) => {
        const amtA = a["TOTAL AMOUNT"] || a.AMOUNT || a["TOTAL PAID"] || 0;
        const amtB = b["TOTAL AMOUNT"] || b.AMOUNT || b["TOTAL PAID"] || 0;
        return amtB - amtA;
      })
      .slice(0, 50);
    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, "TOP 50");
    writeWorkbook(wb, `TopPayers_${today}.xlsx`);
  };

  const reportExpenseHeadWise = () => {
    if (!expenses?.length) {
      toast.error("No expense data available");
      return;
    }
    const wb = XLSX.utils.book_new();
    const groups = {};
    expenses.forEach((e) => {
      const head = e.expenseHead || "UNKNOWN";
      if (!groups[head]) groups[head] = [];
      groups[head].push({
        "EXPENSE ID": toUpper(e.expenseId),
        NAME: toUpper(e.name),
        "INVOICE NO": toUpper(e.invoiceNumber),
        DATE: e.date ? new Date(e.date).toLocaleDateString("en-GB") : "",
        AMOUNT: Number(e.amount) || 0,
        DESCRIPTION: e.description || "",
      });
    });
    Object.entries(groups).forEach(([head, rows]) => {
      const ws = XLSX.utils.json_to_sheet(rows);
      XLSX.utils.book_append_sheet(wb, ws, head.slice(0, 31));
    });
    writeWorkbook(wb, `ExpenseHeadWise_${today}.xlsx`);
  };

  const reportFeeMaster = () => {
    if (!feeMasterDetails?.length) {
      toast.error("No fee master data");
      return;
    }
    const wb = XLSX.utils.book_new();
    const rows = feeMasterDetails.map((f) => ({
      CLASS: toUpper(f.class),
      "ADMISSION FEE": Number(f.admissionFee) || 0,
      "MONTHLY FEE": Number(f.monthlyFee) || 0,
      "DEVELOPMENT CHARGES": Number(f.developmentCharges) || 0,
      "ANNUAL FEE":
        (Number(f.monthlyFee) || 0) * 12 +
        (Number(f.developmentCharges) || 0) +
        (Number(f.admissionFee) || 0),
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, "FEE MASTER");
    writeWorkbook(wb, `FeeMaster_${today}.xlsx`);
  };

  const reportIncomeVsExpense = () => {
    const wb = XLSX.utils.book_new();
    const rows = [
      {
        CATEGORY: "Total Income (Paid Fees + Misc + Admission)",
        AMOUNT: summary.totalIncome,
      },
      { CATEGORY: "Total Expense", AMOUNT: summary.totalExpense },
      { CATEGORY: "Net Balance", AMOUNT: summary.netBalance },
    ];
    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, "P&L Summary");
    writeWorkbook(wb, `IncomeVsExpense_${today}.xlsx`);
  };

  const reportOutstandingDues = () => {
    if (!duesFeeDetails?.length) {
      toast.error("No dues data");
      return;
    }
    const wb = XLSX.utils.book_new();
    const rows = duesFeeDetails.map((s) => {
      const unpaid = s.unpaidMonths || [];
      const monthlyFee = s.monthlyFee || 0;
      const transportFee = s.transportFee || 0;
      const transportable = unpaid.filter((m) => m !== "June").length;
      return {
        "ADMISSION NO": toUpper(s.studentDetails?.admission_no),
        "STUDENT NAME": toUpper(s.studentDetails?.name),
        CLASS: toUpper(s.studentDetails?.currentClass),
        SECTION: toUpper(s.studentDetails?.currentSection),
        "PHONE NO": toUpper(s.studentDetails?.phoneNo),
        "UNPAID MONTHS": unpaid.join(", "),
        "MONTHS COUNT": unpaid.length,
        "MONTHLY DUE": monthlyFee * unpaid.length,
        "TRANSPORT DUE": transportFee * transportable,
        "TOTAL DUE": monthlyFee * unpaid.length + transportFee * transportable,
      };
    });
    rows.sort((a, b) => b["TOTAL DUE"] - a["TOTAL DUE"]);
    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, "OUTSTANDING DUES");
    writeWorkbook(wb, `OutstandingDues_${today}.xlsx`);
  };

  const reportMasterFinancial = () => {
    const wb = XLSX.utils.book_new();

    // Sheet 1: Income Summary
    const incomeRows = [
      {
        SOURCE: "Paid Fees (Tuition + Transport)",
        AMOUNT:
          summary.totalIncome -
          ((miscelleneousFeesDetails || []).reduce(
            (a, s) => a + (Number(s.amount) || 0),
            0,
          ) +
            (admissionFormDetails || []).reduce(
              (a, s) => a + (Number(s.amount) || 0),
              0,
            )),
      },
      {
        SOURCE: "Miscellaneous Fees",
        AMOUNT: (miscelleneousFeesDetails || []).reduce(
          (a, s) => a + (Number(s.amount) || 0),
          0,
        ),
      },
      {
        SOURCE: "Admission Forms",
        AMOUNT: (admissionFormDetails || []).reduce(
          (a, s) => a + (Number(s.amount) || 0),
          0,
        ),
      },
      { SOURCE: "TOTAL INCOME", AMOUNT: summary.totalIncome },
    ];
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(incomeRows),
      "Income Summary",
    );

    // Sheet 2: Expense Summary
    const expenseGroups = {};
    (expenses || []).forEach((e) => {
      const head = e.expenseHead || "UNKNOWN";
      expenseGroups[head] =
        (expenseGroups[head] || 0) + (Number(e.amount) || 0);
    });
    const expenseRows = Object.entries(expenseGroups).map(([head, amt]) => ({
      "EXPENSE HEAD": head,
      AMOUNT: amt,
    }));
    expenseRows.push({
      "EXPENSE HEAD": "TOTAL EXPENSE",
      AMOUNT: summary.totalExpense,
    });
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(expenseRows),
      "Expense Summary",
    );

    // Sheet 3: P&L
    const pnl = [
      { CATEGORY: "Total Income", AMOUNT: summary.totalIncome },
      { CATEGORY: "Total Expense", AMOUNT: summary.totalExpense },
      { CATEGORY: "Net Profit/Loss", AMOUNT: summary.netBalance },
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(pnl), "P&L");

    // Sheet 4: Outstanding Dues Summary
    const duesTotal = (duesFeeDetails || []).reduce((sum, s) => {
      const unpaid = s.unpaidMonths || [];
      const monthlyFee = s.monthlyFee || 0;
      const transportFee = s.transportFee || 0;
      const transportable = unpaid.filter((m) => m !== "June").length;
      return sum + monthlyFee * unpaid.length + transportFee * transportable;
    }, 0);
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet([
        { CATEGORY: "Total Outstanding Dues", AMOUNT: duesTotal },
      ]),
      "Dues",
    );

    writeWorkbook(wb, `MasterFinancial_${today}.xlsx`);
  };

  const reportCollectedBy = () => {
    if (!collectedBySummary?.length) {
      toast.error("No collected-by data available");
      return;
    }
    const wb = XLSX.utils.book_new();
    collectedBySummary.forEach((collector) => {
      const rows = [];
      const sortedDates = [...(collector.dates || [])].sort(
        (a, b) => new Date(a.date) - new Date(b.date),
      );
      sortedDates.forEach((dateEntry) => {
        (dateEntry.paymentModes || []).forEach((pm) => {
          rows.push({
            DATE: new Date(dateEntry.date).toLocaleDateString("en-GB"),
            "PAYMENT MODE": toUpper(pm.mode),
            AMOUNT: pm.totalAmount,
          });
        });
        rows.push({
          DATE: new Date(dateEntry.date).toLocaleDateString("en-GB"),
          "PAYMENT MODE": "TOTAL",
          AMOUNT: dateEntry.dayTotal,
        });
        rows.push({});
      });
      const ws = XLSX.utils.json_to_sheet(rows);
      XLSX.utils.book_append_sheet(
        wb,
        ws,
        toUpper(collector.collectedBy).slice(0, 31),
      );
    });
    writeWorkbook(wb, `CollectedBy_${today}.xlsx`);
  };

  const reportAdmissionStatus = () => {
    if (!admissionFormDetails?.length) {
      toast.error("No admission form data");
      return;
    }
    const wb = XLSX.utils.book_new();
    const submitted = admissionFormDetails.filter((r) => r.submitted);
    const notSubmitted = admissionFormDetails.filter((r) => !r.submitted);

    const formatAdm = (data) =>
      data.map((s) => ({
        "FORM NO": toUpper(s.formNo),
        "APPLY DATE": toUpper(s.applyDate),
        "STUDENT NAME": toUpper(s.student?.name),
        CLASS: toUpper(s.student?.Class),
        "CONTACT NO": toUpper(s.student?.contactNo),
        AMOUNT: Number(s.amount) || 0,
        SUBMITTED: s.submitted ? "YES" : "NO",
      }));

    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(formatAdm(submitted)),
      "Submitted",
    );
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(formatAdm(notSubmitted)),
      "Not Submitted",
    );
    writeWorkbook(wb, `AdmissionStatus_${today}.xlsx`);
  };

  const reportScholarshipImpact = () => {
    if (!paidFeeDetails?.length) {
      toast.error("No paid fee data");
      return;
    }
    const wb = XLSX.utils.book_new();
    const groups = {};
    paidFeeDetails.forEach((s) => {
      const type = s.scholarshipType || "NONE";
      if (!groups[type]) groups[type] = [];
      groups[type].push(formatRow({ ...normalize(s, "paid"), ...s }));
    });
    Object.entries(groups).forEach(([t, rows]) => {
      const ws = XLSX.utils.json_to_sheet(rows);
      XLSX.utils.book_append_sheet(wb, ws, toUpper(t).slice(0, 31));
    });
    writeWorkbook(wb, `ScholarshipImpact_${today}.xlsx`);
  };

  // ----------------------- REPORT CATEGORIES -----------------------
  const reportCategories = [
    { key: "all", label: "All Reports" },
    { key: "collection", label: "Collection" },
    { key: "expense", label: "Expense" },
    { key: "analytics", label: "Analytics" },
    { key: "dues", label: "Dues" },
  ];

  const reports = [
    // Collection
    { cat: "collection", label: "All Records", action: reportAll },
    { cat: "collection", label: "Class Wise", action: reportClassWise },
    {
      cat: "collection",
      label: "Payment Mode Wise",
      action: reportPaymentModeWise,
    },
    { cat: "collection", label: "Month Wise", action: reportMonthWise },
    { cat: "collection", label: "Date Range Report", action: reportDateRange },
    {
      cat: "collection",
      label: "Top 50 Payers",
      action: reportTopPayers,
    },
    {
      cat: "collection",
      label: "Collected By Summary",
      action: reportCollectedBy,
    },
    {
      cat: "collection",
      label: "Admission Form Status",
      action: reportAdmissionStatus,
    },

    // Dues
    {
      cat: "dues",
      label: "Outstanding Dues",
      action: reportOutstandingDues,
    },
    {
      cat: "dues",
      label: "Dues Class Wise",
      action: () => {
        const orig = activeDataset;
        setActiveDataset("dues");
        setTimeout(() => {
          reportClassWise();
          setActiveDataset(orig);
        }, 100);
      },
    },
    {
      cat: "dues",
      label: "Dues Month Wise",
      action: () => {
        const orig = activeDataset;
        setActiveDataset("dues");
        setTimeout(() => {
          reportMonthWise();
          setActiveDataset(orig);
        }, 100);
      },
    },

    // Expense
    {
      cat: "expense",
      label: "Expense Head Wise",
      action: reportExpenseHeadWise,
    },
    {
      cat: "expense",
      label: "All Expenses",
      action: () => {
        const orig = activeDataset;
        setActiveDataset("expense");
        setTimeout(() => {
          reportAll();
          setActiveDataset(orig);
        }, 100);
      },
    },

    // Analytics
    {
      cat: "analytics",
      label: "Income vs Expense",
      action: reportIncomeVsExpense,
    },
    {
      cat: "analytics",
      label: "Master Financial Report",
      action: reportMasterFinancial,
    },
    {
      cat: "analytics",
      label: "Fee Master Structure",
      action: reportFeeMaster,
    },
    {
      cat: "analytics",
      label: "Scholarship Impact",
      action: reportScholarshipImpact,
    },
  ];

  const visibleReports =
    activeCategory === "all"
      ? reports
      : reports.filter((r) => r.cat === activeCategory);

  // ----------------------- RENDER -----------------------
  return (
    <>
      <div id="db-wrapper">
        <main id="page-content">
          <div className="container-fluid my-3 px-3">
            <div className="row g-2">
              <div className="col-lg-8">
                {/* HERO HEADER CARD */}
                <div className="bg-white rounded-4 p-4 mb-2">
                  <div className="bg-light rounded-4 p-4 position-relative overflow-hidden">
                    <div className="d-flex align-items-center">
                      <p className="badge bg-white text-dark rounded-pill px-4 py-2 mb-0 fs-6 fw-medium">
                        <i className="bi bi-cash-coin me-1"></i> Reports &
                        Analytics
                      </p>
                      <div className="ms-4">
                        <h3 className="fw-bold report-title m-0 lh-sm text-dark">
                          Finance Insights Hub
                        </h3>
                        <p className="text-secondary fs-6 fw-medium mb-0 lh-sm">
                          Track collection, dues, expenses & profitability — all
                          in one place.
                        </p>
                      </div>
                    </div>

                    {/* DATASET SELECTOR */}
                    <div className="mt-4">
                      <div className="row g-1">
                        {Object.entries(datasets).map(([key, ds]) => {
                          const isActive = activeDataset === key;
                          const colorMap = {
                            paid: "success",
                            dues: "danger",
                            monthly: "warning",
                            misc: "info",
                            admission: "warning",
                            collectedBy: "info",
                            expense: "danger",
                            master: "success",
                          };
                          const color = colorMap[key];
                          return (
                            <div className="col-md-3 col-6" key={key}>
                              <div
                                onClick={() => setActiveDataset(key)}
                                className={`p-3 rounded-4 cursor-pointer position-relative ${
                                  isActive
                                    ? `bg-${color} text-white`
                                    : `bg-${color}-soft`
                                }`}
                              >
                                {isActive && (
                                  <span className="position-absolute top-0 end-0 m-2">
                                    <i className="bi bi-check-circle-fill text-white fs-5"></i>
                                  </span>
                                )}
                                <p
                                  className={`m-0 fw-normal fs-5 lh-sm ${isActive ? "text-white" : "text-dark"}`}
                                >
                                  {ds.label}
                                </p>
                                <h3
                                  className={`fw-bold m-0 lh-1 mt-1 ${isActive ? "text-white" : "text-dark"}`}
                                >
                                  {(ds.data?.length || 0).toLocaleString(
                                    "en-IN",
                                  )}
                                </h3>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>

                {/* FILTERS CARD */}
                <div className="bg-white rounded-4 p-4 mb-2">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <p className="m-0 fw-medium lh-1 text-dark fs-5">
                      <i className="bi bi-funnel-fill me-2 text-secondary"></i>
                      Refine Results
                    </p>
                    <span
                      onClick={() => {
                        setSelectedClass("");
                        setSelectedSection("");
                        setSelectedMonth("");
                        setSelectedPaymentMode("");
                        setFromDate("");
                        setToDate("");
                        setSearchTerm("");
                      }}
                      className="badge bg-danger-soft text-dark rounded-3 px-3 py-2 cursor-pointer fw-medium"
                    >
                      <i className="bi bi-arrow-counterclockwise me-1"></i>{" "}
                      Reset
                    </span>
                  </div>

                  {/* Search */}
                  <div className="bg-light rounded-4 mb-3">
                    <form
                      className="d-flex align-items-center"
                      onSubmit={(e) => e.preventDefault()}
                    >
                      <span className="position-absolute ps-3 search-icon">
                        <i className="bi bi-filter-circle-fill ms-1" />
                      </span>
                      <input
                        type="search"
                        className="form-control ps-7 rounded-4 border-0 bg-light"
                        placeholder="Search receipt, name, admission no, mode..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </form>
                  </div>

                  {/* Class & Section */}
                  <div className="bg-light rounded-4 py-3 px-4 mb-2">
                    <p className="m-0 fw-medium lh-1 text-dark mb-2">
                      Class &amp; Section
                    </p>
                    <div className="row g-2">
                      <div className="col-md-6">
                        <div className="bg-white rounded-3 px-3 py-1 d-flex align-items-center h-100">
                          <i className="bi bi-mortarboard-fill text-secondary me-2"></i>
                          <Select
                            className="flex-grow-1 h-100"
                            classNamePrefix="rs"
                            isClearable
                            placeholder="All Classes"
                            value={
                              selectedClass
                                ? { value: selectedClass, label: selectedClass }
                                : null
                            }
                            onChange={(opt) => {
                              setSelectedClass(opt ? opt.value : "");
                              setSelectedSection("");
                            }}
                            options={classOptions}
                            styles={selectStyles}
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="bg-white rounded-3 px-3 py-1 d-flex align-items-center h-100">
                          <i className="bi bi-bookmark-fill text-secondary me-2"></i>
                          <Select
                            className="flex-grow-1 h-100"
                            classNamePrefix="rs"
                            isClearable
                            isDisabled={!selectedClass}
                            placeholder={
                              selectedClass
                                ? "All Sections"
                                : "Select class first"
                            }
                            value={
                              selectedSection
                                ? {
                                    value: selectedSection,
                                    label: selectedSection,
                                  }
                                : null
                            }
                            onChange={(opt) =>
                              setSelectedSection(opt ? opt.value : "")
                            }
                            options={sectionOptions}
                            styles={selectStyles}
                            noOptionsMessage={() => "No sections available"}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Month & Payment Mode */}
                  <div className="bg-light rounded-4 py-3 px-4 mb-2">
                    <p className="m-0 fw-medium lh-1 text-dark mb-2">
                      Month &amp; Payment Mode
                    </p>
                    <div className="row g-2">
                      <div className="col-md-6">
                        <div className="bg-white rounded-3 px-3 py-1 d-flex align-items-center h-100">
                          <i className="bi bi-calendar-month-fill text-secondary me-2"></i>
                          <Select
                            className="flex-grow-1 h-100"
                            classNamePrefix="rs"
                            isClearable
                            placeholder="All Months"
                            value={
                              selectedMonth
                                ? {
                                    value: selectedMonth,
                                    label:
                                      selectedMonth.charAt(0).toUpperCase() +
                                      selectedMonth.slice(1),
                                  }
                                : null
                            }
                            onChange={(opt) =>
                              setSelectedMonth(opt ? opt.value : "")
                            }
                            options={monthOptions}
                            styles={selectStyles}
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="bg-white rounded-3 px-3 py-1 d-flex align-items-center h-100">
                          <i className="bi bi-credit-card-2-front-fill text-secondary me-2"></i>
                          <Select
                            className="flex-grow-1 h-100"
                            classNamePrefix="rs"
                            isClearable
                            placeholder="All Modes"
                            value={
                              selectedPaymentMode
                                ? {
                                    value: selectedPaymentMode,
                                    label: selectedPaymentMode,
                                  }
                                : null
                            }
                            onChange={(opt) =>
                              setSelectedPaymentMode(opt ? opt.value : "")
                            }
                            options={paymentModeOptions}
                            styles={selectStyles}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Date Range */}
                  <div className="bg-light rounded-4 py-3 px-4">
                    <p className="m-0 fw-medium lh-1 text-dark mb-2">
                      Transaction Date Range
                    </p>
                    <div className="row g-2">
                      <div className="col-md-6">
                        <div className="bg-white rounded-3 px-3 py-2 d-flex align-items-center">
                          <i className="bi bi-calendar-event text-secondary me-2"></i>
                          <input
                            type="date"
                            className="form-control border-0 p-0 fs-6 bg-transparent"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                          />
                        </div>
                        <p className="m-0 text-secondary fs-6 mt-1 ms-2 lh-1">
                          From
                        </p>
                      </div>
                      <div className="col-md-6">
                        <div className="bg-white rounded-3 px-3 py-2 d-flex align-items-center">
                          <i className="bi bi-calendar-check text-secondary me-2"></i>
                          <input
                            type="date"
                            className="form-control border-0 p-0 fs-6 bg-transparent"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                          />
                        </div>
                        <p className="m-0 text-secondary fs-6 mt-1 ms-2 lh-1">
                          To
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* LIVE SNAPSHOT */}
                {/* LIVE SNAPSHOT */}
                <div className="bg-white rounded-4 p-4 mb-2">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <p className="m-0 fw-medium lh-1 text-dark fs-5">
                      <i className="bi bi-pie-chart-fill me-2 text-secondary"></i>
                      Live Snapshot
                    </p>
                    <span className="badge bg-success-soft text-dark rounded-3 px-4 py-2 fw-medium">
                      <i className="bi bi-broadcast me-1"></i> Real-time
                    </span>
                  </div>

                  <div className="row g-2">
                    {/* Donut: Income vs Expense */}
                    <div className="col-md-5">
                      <div className="bg-danger-soft rounded-4 p-4 h-100 d-flex flex-column align-items-center justify-content-center position-relative">
                        <svg viewBox="0 0 100 100" className="w-75">
                          <circle
                            cx="50"
                            cy="50"
                            r="40"
                            fill="none"
                            stroke="#fff"
                            strokeWidth="12"
                          />
                          <circle
                            cx="50"
                            cy="50"
                            r="40"
                            fill="none"
                            stroke="#198754"
                            strokeWidth="12"
                            strokeDasharray={`${summary.totalIncome + summary.totalExpense ? (summary.totalIncome / (summary.totalIncome + summary.totalExpense)) * 251.2 : 0} 251.2`}
                            transform="rotate(-90 50 50)"
                          />
                          <circle
                            cx="50"
                            cy="50"
                            r="40"
                            fill="none"
                            stroke="#dc3545"
                            strokeWidth="12"
                            strokeDasharray={`${summary.totalIncome + summary.totalExpense ? (summary.totalExpense / (summary.totalIncome + summary.totalExpense)) * 251.2 : 0} 251.2`}
                            strokeDashoffset={`${summary.totalIncome + summary.totalExpense ? -((summary.totalIncome / (summary.totalIncome + summary.totalExpense)) * 251.2) : 0}`}
                            transform="rotate(-90 50 50)"
                          />
                        </svg>
                        <div className="position-absolute text-center">
                          <p className="m-0 fs-6 text-secondary lh-1">
                            Net Balance
                          </p>
                          <h4
                            className={`m-0 fw-bold lh-1 mt-1 ${summary.netBalance >= 0 ? "text-success" : "text-danger"}`}
                          >
                            ₹
                            {Math.abs(summary.netBalance).toLocaleString(
                              "en-IN",
                            )}
                          </h4>
                        </div>
                      </div>
                    </div>

                    {/* Stat Grid */}
                    <div className="col-md-7">
                      <div className="row g-1 h-100">
                        <div className="col-6">
                          <div className="bg-success-soft rounded-4 p-4 h-100">
                            <i className="bi bi-bank text-dark fs-3 d-block lh-1"></i>
                            <p className="m-0 fs-6 text-secondary lh-1 mt-2">
                              Total Collection
                            </p>
                            <h4 className="m-0 fw-bold text-dark">
                              ₹{summary.totalIncome.toLocaleString("en-IN")}
                            </h4>
                            <p className="m-0 fs-6 text-secondary mt-1 lh-1">
                              Tuition + transport + misc
                            </p>
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="bg-danger-soft rounded-4 p-4 h-100">
                            <i className="bi bi-hourglass-split text-dark fs-3 d-block lh-1"></i>
                            <p className="m-0 fs-6 text-secondary lh-1 mt-2">
                              Total Dues
                            </p>
                            <h4 className="m-0 fw-bold text-dark">
                              ₹
                              {(duesFeeDetails || [])
                                .reduce((sum, s) => {
                                  const unpaid = s.unpaidMonths || [];
                                  const mf = s.monthlyFee || 0;
                                  const tf = s.transportFee || 0;
                                  const transportable = unpaid.filter(
                                    (m) => m !== "June",
                                  ).length;
                                  return (
                                    sum +
                                    mf * unpaid.length +
                                    tf * transportable
                                  );
                                }, 0)
                                .toLocaleString("en-IN")}
                            </h4>
                            <p className="m-0 fs-6 text-secondary mt-1 lh-1">
                              {(duesFeeDetails || []).length} students pending
                            </p>
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="bg-warning-soft rounded-4 p-4 h-100">
                            <i className="bi bi-receipt text-dark fs-3 d-block lh-1"></i>
                            <p className="m-0 fs-6 text-secondary lh-1 mt-2">
                              Today's Collection
                            </p>
                            <h4 className="m-0 fw-bold text-dark">
                              ₹
                              {(monthlyFees || [])
                                .reduce((sum, s) => {
                                  const fd = s.feeDetails || {};
                                  let t = 0;
                                  if (fd.developmentCharges?.paid)
                                    t += fd.developmentCharges.TotalAmt || 0;
                                  if (fd.admissionFee?.paid)
                                    t += fd.admissionFee.TotalAmt || 0;
                                  (fd.monthPaid || []).forEach(
                                    (m) => (t += m.TotalAmt || 0),
                                  );
                                  return sum + t;
                                }, 0)
                                .toLocaleString("en-IN")}
                            </h4>
                            <p className="m-0 fs-6 text-secondary mt-1 lh-1">
                              {(monthlyFees || []).length} receipts today
                            </p>
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="bg-info-soft rounded-4 p-4 h-100">
                            <i className="bi bi-cash-coin text-dark fs-3 d-block lh-1"></i>
                            <p className="m-0 fs-6 text-secondary lh-1 mt-2">
                              Miscellaneous
                            </p>
                            <h4 className="m-0 fw-bold text-dark">
                              ₹
                              {(miscelleneousFeesDetails || [])
                                .reduce(
                                  (sum, s) => sum + (Number(s.amount) || 0),
                                  0,
                                )
                                .toLocaleString("en-IN")}
                            </h4>
                            <p className="m-0 fs-6 text-secondary mt-1 lh-1">
                              {(miscelleneousFeesDetails || []).length}{" "}
                              transactions
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* RIGHT - Reports Panel */}
              <div className="col-lg-4">
                <div className="mb-3">
                  <div className="mt-2 ms-4">
                    <nav aria-label="breadcrumb">
                      <ol className="breadcrumb pe-3">
                        <li className="breadcrumb-item">
                          <a href="#">Reports & Analytics</a>
                        </li>
                        <li
                          className="breadcrumb-item active"
                          aria-current="page"
                        >
                          Finance
                        </li>
                      </ol>
                    </nav>
                  </div>
                </div>

                <div className="p-4 bg-white rounded-4">
                  <div className="bg-danger-soft rounded-4 p-3 d-flex align-items-center mb-3">
                    <div className="avatar-group">
                      {[1, 2, 3, 4, 5].map((num) => (
                        <span className="avatar avatar-md" key={num}>
                          <img
                            alt={`avatar-${num}`}
                            src={`/assets/images/students/${num}.jpg`}
                            className="rounded-circle"
                          />
                        </span>
                      ))}
                    </div>
                    <h3 className="fw-bold m-0 text-center fs-4 ms-3">
                      {filteredData.length.toLocaleString("en-IN")}
                    </h3>
                  </div>

                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                      {currentLoading ? (
                        <div
                          className="spinner-border text-dark data-loader-spinner"
                          role="status"
                        ></div>
                      ) : (
                        <img src="/assets/images/folder.svg" alt="folder" />
                      )}
                      <p className="my-0 fw-semibold lh-1 fs-5 text-dark mx-3">
                        {datasets[activeDataset].label}
                        <br />
                        <span className="text-secondary fs-6 fw-medium lh-sm">
                          {reports.length} Reports Available
                        </span>
                      </p>
                    </div>
                    <span
                      onClick={reportAll}
                      className="badge bg-pink rounded-3 px-4 py-2 me-1 mt-1 cursor-pointer"
                    >
                      Export
                    </span>
                  </div>

                  {/* Category Tabs */}
                  <div className="mt-3 d-flex flex-wrap gap-1">
                    {reportCategories.map((cat) => (
                      <div
                        key={cat.key}
                        className={`bg-dark-soft rounded-3 cursor-pointer px-3 py-1 fs-6 ${
                          activeCategory === cat.key
                            ? "bg-warning text-white border-warning"
                            : ""
                        }`}
                        onClick={() => setActiveCategory(cat.key)}
                      >
                        {cat.label}
                      </div>
                    ))}
                  </div>

                  {/* Report Buttons */}
                  <div className="px-4 py-2 mt-3 bg-light rounded-4">
                    <li className="list-group-item px-0">
                      <a
                        className="d-flex align-items-center text-inherit mb-0 justify-content-between"
                        data-bs-toggle="collapse"
                        href="#reportsCollapse"
                        role="button"
                        aria-expanded="true"
                        aria-controls="reportsCollapse"
                      >
                        <p className="m-0 fw-medium lh-sm text-dark">
                          Available Reports ({visibleReports.length})
                        </p>
                        <span className="chevron-arrow ms-4">
                          <i className="bi bi-chevron-down fs-4" />
                        </span>
                      </a>
                      <div className="collapse" id="reportsCollapse">
                        <div className="py-3">
                          <div className="rounded-4 d-flex flex-wrap">
                            {visibleReports.map((r, i) => (
                              <span
                                key={i}
                                onClick={r.action}
                                className="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                              >
                                {r.label}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </li>
                  </div>

                  {/* Quick Action Cards */}
                  <div className="mt-2">
                    <div className="row g-1">
                      <div className="col-6">
                        <div
                          onClick={reportMasterFinancial}
                          className="p-3 bg-success-soft rounded-4 cursor-pointer"
                        >
                          <p className="m-0 text-center fw-medium lh-sm text-dark">
                            Master
                          </p>
                          <p className="text-center fs-5 m-0 lh-1">
                            Financial Report
                          </p>
                        </div>
                      </div>
                      <div className="col-6">
                        <div
                          onClick={reportIncomeVsExpense}
                          className="p-3 bg-info-soft rounded-4 cursor-pointer"
                        >
                          <p className="m-0 text-center fw-medium lh-sm text-dark">
                            P&amp;L
                          </p>
                          <p className="text-center fs-5 m-0 lh-1">
                            Income vs Expense
                          </p>
                        </div>
                      </div>
                      <div className="col-12">
                        <div
                          onClick={reportOutstandingDues}
                          className="p-3 bg-danger-soft rounded-4 cursor-pointer"
                        >
                          <p className="m-0 text-center fw-medium lh-sm text-dark">
                            Outstanding Dues Report
                          </p>
                          <p className="text-center fs-5 m-0 lh-1">
                            Sorted by Highest Fee Dues Pending
                          </p>
                        </div>
                      </div>
                      <div className="col-6">
                        <div
                          onClick={reportCollectedBy}
                          className="p-3 bg-warning-soft rounded-4 cursor-pointer"
                        >
                          <p className="m-0 text-center fw-medium lh-sm text-dark">
                            Collected By
                          </p>
                          <p className="text-center fs-5 m-0 lh-1">
                            Per Collector
                          </p>
                        </div>
                      </div>
                      <div className="col-6">
                        <div
                          onClick={reportExpenseHeadWise}
                          className="p-3 bg-warning-soft rounded-4 cursor-pointer"
                        >
                          <p className="m-0 text-center fw-medium lh-sm text-dark">
                            Expenses
                          </p>
                          <p className="text-center fs-5 m-0 lh-1">Head Wise</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}

export default FinanceReports;
